import {
  IFundRankingClass,
  IFundRankingYear,
} from '@mais-retorno-front/core-business/entities';
import { makeGetFundRankingClasses } from '@mais-retorno-front/core-business/factories';
import { IFundRanking } from '@mais-retorno-front/libs/util-models';
import { AxiosInstance } from 'axios';
import { IGetRankingClassesResponse } from './get-fund-classes';

export interface GetFundRankingUseCaseResult {
  title: string;
  description: string;
  classesLevel1: IFundRankingClass[];
  classesLevel2: IFundRankingClass[];
  classesLevel3: IFundRankingClass[];
  showClassLevel2: boolean;
  showClassLevel3: boolean;
  showYears: boolean;
  showMonths: boolean;
  years: IFundRankingClass[];
  months: IFundRankingClass[];
  breadcrumbs: IBreadcrumb[];
  funds: IFundRanking[];
  canonical: string;
  seoDescription: string;
}

interface IBreadcrumb {
  label: string;
  url: string;
}

interface GetFundRankingUseCaseProps {
  fundClass: string;
  year: string;
  month: string;
  pageSize?: number;
}

export class GetFundRankingUseCase {
  private YEARS: IFundRankingClass[] = Array(
    new Date().getFullYear() - 1996 + 1
  )
    .fill(1996)
    .map((e, i) => ({
      name: (e + i).toString(),
      selected: false,
      slug: (e + i).toString(),
    }));

  private MONTHS: IFundRankingYear[] = [
    { name: 'JAN', slug: 'janeiro', selected: false, value: 1 },
    { name: 'FEV', slug: 'fevereiro', selected: false, value: 2 },
    { name: 'MAR', slug: 'marco', selected: false, value: 3 },
    { name: 'ABR', slug: 'abril', selected: false, value: 4 },
    { name: 'MAI', slug: 'maio', selected: false, value: 5 },
    { name: 'JUN', slug: 'junho', selected: false, value: 6 },
    { name: 'JUL', slug: 'julho', selected: false, value: 7 },
    { name: 'AGO', slug: 'agosto', selected: false, value: 8 },
    { name: 'SET', slug: 'setembro', selected: false, value: 9 },
    { name: 'OUT', slug: 'outubro', selected: false, value: 10 },
    { name: 'NOV', slug: 'novembro', selected: false, value: 11 },
    { name: 'DEZ', slug: 'dezembro', selected: false, value: 12 },
  ];

  private initialBreadcrumb: IBreadcrumb = {
    label: 'Melhores Fundos',
    url: '/melhores-fundos',
  };

  constructor(private readonly http: AxiosInstance) {}

  async handle({
    fundClass,
    year,
    month,
    pageSize = 50,
  }: GetFundRankingUseCaseProps): Promise<GetFundRankingUseCaseResult> {
    const [classLevel1, classLevel2, classLevel3] = fundClass.split('_');

    let defaultRankingProps: GetFundRankingUseCaseResult = {
      title: null,
      description: null,
      classesLevel1: [],
      classesLevel2: [],
      classesLevel3: [],
      showClassLevel2: !!classLevel1,
      showClassLevel3: !!classLevel2,
      showYears: !!classLevel1,
      showMonths: !!year,
      years: this.getYears(year, fundClass),
      months: this.getMonths(year, month, fundClass),
      breadcrumbs: [this.initialBreadcrumb],
      funds: [],
      canonical: this.createCanonical(fundClass, year, month),
      seoDescription: null,
    };

    const classes = await makeGetFundRankingClasses().handle();

    const { data: fundsRanking } = await this.http.get(
      '/ranking/ranking_date',
      {
        params: {
          unique_slug_n1: classLevel1,
          unique_slug_n2: classLevel2,
          unique_slug_n3: classLevel3,
          _page_size: pageSize,
          _page: 1,
          year,
          month: this.getMonthsValue(month),
        },
      }
    );

    defaultRankingProps.classesLevel1 = classes.map((item) => ({
      ...item,
      selected: item.slug === classLevel1,
    }));

    if (classLevel1) {
      const { data: classesN2 } = await this.http.get<
        IGetRankingClassesResponse[]
      >('/ranking/subclasses_n2', {
        params: { unique_slug_n1: classLevel1 },
      });

      defaultRankingProps.classesLevel2 = classesN2.map((item) => ({
        ...item,
        selected: item.slug === `${classLevel1}_${classLevel2}`,
      }));
    }

    if (classLevel2) {
      const { data: classesN3 } = await this.http.get<
        IGetRankingClassesResponse[]
      >('/ranking/subclasses_n3', {
        params: { unique_slug_n1: classLevel1, unique_slug_n2: classLevel2 },
      });

      defaultRankingProps.classesLevel3 = classesN3.map((item) => ({
        ...item,
        selected: item.slug === `${classLevel1}_${classLevel2}_${classLevel3}`,
      }));
    }

    const { name: classLevel1Name } = this.getSelectedClass(
      defaultRankingProps.classesLevel1,
      classLevel1
    );
    const { name: classLevel2Name } = this.getSelectedClass(
      defaultRankingProps.classesLevel2,
      `${classLevel1}_${classLevel2}`
    );
    const { name: classLevel3Name } = this.getSelectedClass(
      defaultRankingProps.classesLevel3,
      `${classLevel1}_${classLevel2}_${classLevel3}`
    );

    return {
      ...defaultRankingProps,
      funds: fundsRanking,
      title: this.createTitle(
        classLevel1Name,
        classLevel2Name,
        classLevel3Name,
        year,
        month
      ),
      description: this.createDescription(
        classLevel1Name,
        classLevel2Name,
        classLevel3Name,
        year,
        month
      ),
      breadcrumbs: this.getBreadcrumb(
        defaultRankingProps.classesLevel1.find(({ selected }) => selected),
        defaultRankingProps.classesLevel2.find(({ selected }) => selected),
        defaultRankingProps.classesLevel3.find(({ selected }) => selected),
        year,
        month
      ),
      seoDescription: this.createSEODescription(
        classLevel1Name,
        classLevel2Name,
        classLevel3Name,
        year,
        month
      ),
    };
  }

  private getYears(selectedYear: string, fundClass: string) {
    const slugWithFundClass = fundClass ? `${fundClass}/` : '';

    return this.YEARS.map((item) => ({
      ...item,
      name: item.name,
      slug: `${slugWithFundClass}${item.slug}`,
      selected: item.slug === selectedYear,
    }));
  }

  private getMonths(year: string, selectedMonth: string, fundClass: string) {
    const currentYear = new Date().getFullYear().toString();
    const currentMonth = new Date().getMonth();

    const months = this.MONTHS.map((item) => ({
      ...item,
      slug: `${fundClass}/${year}/${item.slug}`,
      selected: item.slug === selectedMonth,
    }));

    return year && year === currentYear
      ? months.slice(0, currentMonth + 1)
      : months;
  }

  private getMonthsValue(monthSlug: string) {
    if (!monthSlug) return null;

    return this.MONTHS.find((item) => item.slug === monthSlug).value;
  }

  private getSelectedClass(list: IFundRankingClass[], slug: string) {
    const defaultClass = { name: null };

    if (!list || !list.length) return defaultClass;

    const selected = list.find((item) => item.slug === slug);

    return selected || defaultClass;
  }

  private createTitle(
    classLevel1Name: string,
    classLevel2Name: string,
    classLevel3Name: string,
    year: string,
    month: string
  ): string {
    let title = 'Melhores Fundos de Investimentos';

    if (classLevel1Name) title = `Melhores Fundos de ${classLevel1Name}`;
    if (classLevel2Name) title = `Melhores Fundos de ${classLevel2Name}`;
    if (classLevel3Name) title = `Melhores Fundos de ${classLevel3Name}`;

    if (month) {
      title = `${title} de ${month}`;
    }

    if (year) {
      title = `${title} de ${year}`;
    }

    return title;
  }

  private createDescription(
    classLevel1Name: string,
    classLevel2Name: string,
    classLevel3Name: string,
    year: string,
    month: string
  ): string {
    if (!classLevel1Name)
      return `Confira abaixo os 10 fundos com maior retorno dos <strong>últimos 6 meses.</strong> <br /> Neste ranking, <strong>utilizamos alguns critérios, que estão listados logo abaixo:</strong>`;

    let description = `Confira o ranking com os melhores fundos da classe`;

    if (classLevel3Name) {
      description = `${description} <strong>${classLevel3Name}</strong>`;
    } else if (classLevel2Name) {
      description = `${description} <strong>${classLevel2Name}</strong>`;
    } else if (classLevel1Name) {
      description = `${description} <strong>${classLevel1Name}</strong>`;
    }

    description = `${description} mais rentáveis`;

    if (month) {
      description = `${description} <strong>de ${month}</strong>`;
    }

    if (year) {
      description = `${description} <strong>de ${year}</strong>`;
    } else {
      description = `${description} <strong>dos últimos 6 meses.</strong>`;
    }

    description = `${description} <br>Você pode mudar as classes ou escolher um ano e mês para ter um ranking mais detalhado.`;

    return description;
  }

  private createSEODescription(
    classLevel1Name: string,
    classLevel2Name: string,
    classLevel3Name: string,
    year: string,
    month: string
  ): string {
    if (!classLevel1Name)
      return `Confira abaixo os 10 fundos com maior retorno dos últimos 6 meses. Neste ranking, utilizamos alguns critérios, que estão listados logo abaixo:`;

    let description = `Confira o ranking com os melhores fundos de`;

    if (classLevel3Name) {
      description = `${description} ${classLevel3Name}`;
    } else if (classLevel2Name) {
      description = `${description} ${classLevel2Name}`;
    } else if (classLevel1Name) {
      description = `${description} ${classLevel1Name}`;
    }

    description = `${description} mais rentáveis`;

    if (month) {
      description = `${description} de ${month}`;
    }

    if (year) {
      description = `${description} de ${year}`;
    } else {
      description = `${description} dos últimos 6 meses.`;
    }

    description = `${description} Você pode mudar as classes ou escolher um ano e mês para ter um ranking mais detalhado.`;

    return description;
  }

  private getBreadcrumb(
    class1: IFundRankingClass,
    class2: IFundRankingClass,
    class3: IFundRankingClass,
    year: string,
    month: string
  ): IBreadcrumb[] {
    const breadcrumbs = [this.initialBreadcrumb];
    const classesBreadcrumb: IBreadcrumb = { label: '', url: '' };

    if (class3) {
      classesBreadcrumb.label = `${classesBreadcrumb.label} ${class3.name}`;
      classesBreadcrumb.url = `${this.initialBreadcrumb.url}/${class3.slug}`;
    } else if (class2) {
      classesBreadcrumb.label = `${classesBreadcrumb.label} ${class2.name}`;
      classesBreadcrumb.url = `${this.initialBreadcrumb.url}/${class2.slug}`;
    } else if (class1) {
      classesBreadcrumb.label = `${classesBreadcrumb.label} ${class1.name}`;
      classesBreadcrumb.url = `${this.initialBreadcrumb.url}/${class1.slug}`;
    }

    if (classesBreadcrumb.url) {
      breadcrumbs.push(classesBreadcrumb);
    }

    if (year) {
      breadcrumbs.push({
        label: year,
        url: `${classesBreadcrumb.url}/${year}`,
      });
    }

    if (month) {
      breadcrumbs.push({
        label: month,
        url: `${classesBreadcrumb.url}/${year}/${month}`,
      });
    }

    return breadcrumbs;
  }

  private createCanonical(
    fundClass: string,
    year: string,
    month: string
  ): string {
    let canonical = fundClass;

    if (!!year) {
      canonical = `${canonical}/${year}`;
    }

    if (!!month) {
      canonical = `${canonical}/${month}`;
    }

    return canonical;
  }
}
